import { ISamlIndetityProvider } from 'models/identity-provider/identity-provider';
import { ListIdentityProviderConfigurationResponse, Convert as ListIdentityProviderConfigurationResponseConvert } from 'models/schema-generated-models/api-response-bodies/list-identity-provider-configuration-response';
import { SuccessMessageResponse } from 'models/schema-generated-models/api-response-bodies/success-message-response';
import { Configuration as IConfiguration } from 'models/schema-generated-models/idp/configuration';
import { Convert as ConfigurationsConvert } from 'models/schema-generated-models/idp/configurations';
import { SSOAPIContextParser } from 'services/api-context-service/sso-api-context-parser';
import { SSOAPINotFoundError, SSOAPIUnexpectedResponseBody } from 'services/errors';
import { BaseService } from 'services/sso-api/base-service';
import { ClientResponse } from '../client/client-response';

export enum IdentityProviderRoute {
    IDP_REMOVE = '/idp/remove-identity-provider',
    IDP_CREATE = '/idp/add-identity-provider',
    IDP_LIST_CONFIGURATIONS = '/idp/list-identity-provider-configuration',
    IDP_TOGGLE_STATUS = '/idp/toggle-identity-provider'
}

export class IdentityProviderService extends BaseService {

    deleteIdentityProvider = async (configName: string, idpName: string): Promise<SuccessMessageResponse> => {
        const response: ClientResponse = await this.client.delete(IdentityProviderRoute.IDP_REMOVE, { body: JSON.stringify({ 'provider_name': idpName, 'configuration_name': configName }) });;

        if (response && response.status < 300 && response.status >= 200) {
            const body = await response.json();
            return body;
        }

        const responseAPIContext = SSOAPIContextParser.getSSOAPIContextFromResponse(response);

        if (response.status === 404) {
            throw new SSOAPINotFoundError(responseAPIContext);
        }

        const body = await response.text();
        throw this.processGeneralAPIError(response, body, responseAPIContext);
    };

    getConfigurations = async (): Promise<IConfiguration[]> => {
        const response: ClientResponse = await this.client.get(IdentityProviderRoute.IDP_LIST_CONFIGURATIONS);
        const body = await response.text();
        const responseAPIContext = SSOAPIContextParser.getSSOAPIContextFromResponse(response);

        if (response && response.status < 300 && response.status >= 200) {
            let configurationResponse: ListIdentityProviderConfigurationResponse;
            try {
                configurationResponse = ListIdentityProviderConfigurationResponseConvert.toListIdentityProviderConfigurationResponse(body);
                if (!configurationResponse.Configurations) {
                    return [];
                }
                return ConfigurationsConvert.toConfigurations(configurationResponse.Configurations);
            } catch(error) {
                throw new SSOAPIUnexpectedResponseBody(responseAPIContext, 'Unexpected response body from successful return.', { cause: error as Error });
            }
        }

        if (response.status === 404) {
            throw new SSOAPINotFoundError(responseAPIContext);
        }

        throw this.processGeneralAPIError(response, body, responseAPIContext);
    };

    createIdentityProvider = async (idp: ISamlIndetityProvider): Promise<SuccessMessageResponse> => {
        const response: ClientResponse = await this.client.post(IdentityProviderRoute.IDP_CREATE, { body: JSON.stringify(idp) });
        if (response && response.status < 300 && response.status >= 200) {
            const body = await response.json();
            return body;
        }

        const responseAPIContext = SSOAPIContextParser.getSSOAPIContextFromResponse(response);
        const body = await response.text();
        throw this.processGeneralAPIError(response, body, responseAPIContext);
    };

    toggleIdentityProviderStatus = async (configName: string, idpName: string, status: boolean): Promise<SuccessMessageResponse> => {
        const response: ClientResponse = await this.client.post(IdentityProviderRoute.IDP_TOGGLE_STATUS, { body: JSON.stringify({ 'provider_name': idpName, 'status': status, 'configuration_name': configName }) });

        if (response && response.status < 300 && response.status >= 200) {
            return await response.json();
        }
        const responseAPIContext = SSOAPIContextParser.getSSOAPIContextFromResponse(response);
        if (response.status === 404) {
            throw new SSOAPINotFoundError(responseAPIContext);
        }

        const body = await response.text();
        throw this.processGeneralAPIError(response, body, responseAPIContext);
    };
}
